// перевіряє строку на пробіл
import React from "react";

import {
  Keyboard,
  KeyPressType,
  Locales,
  RewardEnum,
  RewardModalContent,
} from "@typings/global.types";

export const isNotSpace = (str: string) => {
  return str !== " ";
};
export const isLetter = (str: string) => {
  if (!str) return false;
  return str.toUpperCase() !== str.toLowerCase();
};
export const isInLowercase = (str: string) => {
  return str === str.toLowerCase();
};
export const isInUppercase = (str: string) => {
  return str === str.toUpperCase();
};
// isKyr - перевіряє строку на кирилицю
export const isKyr = (str: string) => /[а-я]/i.test(str);
// Виводим текст в залежності від кількості помилок
export const title = (err: number): string => {
  if (err >= 3) {
    return "Ой! Забагато помилок";
  }

  return "Вправа завершена!";
};
// circle progress indicator svg, видає число для індикатора підрахунку вправ
export const strokeDashoffset = (
  number: number,
  numberOf: number,
  max = 150,
): number => {
  if (number > 0) return max - (max / number) * numberOf;

  return max;
};
// змінює строку в cameCase
export const toCamelCase = (str: string): string => {
  let res = "";
  str
    .replace(/_/g, " ")
    .split(" ")
    .forEach(function (el, idx) {
      const add = el.toLowerCase();
      res += idx === 0 ? add : add[0].toUpperCase() + add.slice(1);
    });

  return res;
};
// змінює назву ключів і вертає новий обєкт
export const renameKeysObject = (obj: any): any => {
  const newObj = { ...obj };
  Object.keys(newObj).forEach((e) => {
    newObj[toCamelCase(e)] = newObj[e];
    if (toCamelCase(e) !== e) delete newObj[e];
  });

  return newObj;
};
// checkLetterLength - перевіряє кінець тексту
export const checkLetterLength = (lettersArr: string[]): boolean => {
  return lettersArr.length === 0;
};
// вертаємо швидкість в залежності від ratio (коефіціент, який різний для Української і рос)
export const speed2CurrentUnits = (speed: number, ratio: number): number => {
  return Math.floor(speed / ratio);
};
// перетворення унікода
export const convertUnicode = (str: string): string => {
  return str.replace(/\\+u([0-9a-fA-F]{4})/g, (a, b) =>
    String.fromCharCode(parseInt(b, 16)),
  );
};
// Підрахунок довжини анімації в мілесекундах
export const calcAnimateDelay = (animateTiming: [number, number]) => {
  // Частота кадрів анімації
  const fps = 60;

  return Math.round(((animateTiming[1] - animateTiming[0]) / fps) * 1000);
};
// вираховує вітцотки з швидкості
export const calculatePercents = (speed: number): number => {
  if (speed <= 100) return 10;
  if (speed <= 150) return 15;
  if (speed <= 200) return 30;
  if (speed <= 250) return 50;
  if (speed <= 300) return 70;
  return 80;
};
// додає стилі до блоку
export const addStyle = (element: any, style: string) => {
  element.setAttribute("style", `${style}`);
};

export const transformKeypressEventToKeyPressType = (
  event: KeyboardEvent,
): KeyPressType => {
  let value = event.key ?? String.fromCharCode(event.keyCode);
  // фікс для nbsp (альт + спейс)
  if (value === " ") {
    value = " ";
  }
  // фикс для firefox 11.0
  // если значение undefined, нельзя в этом браузере его сравнить со строкой undefined
  // todo перевірити
  if (`${event.key}` === "undefined") {
    value = "undefined";
  }

  const isPrintable = event.key.length === 1;
  const isSpace = value === " ";
  // todo move to config
  const ignoredKeyCodes = [9];

  return {
    value,
    isLetter: isPrintable && value.toLowerCase() !== value.toUpperCase(),
    isNumeric: Number(event.key) >= 0 && Number(event.key) <= 9,
    isUpperCase: !isSpace ? value === value.toUpperCase() : false,
    isPrintable,
    isDeadKey: event.key === "Dead",
    keyCode: event.keyCode,
    containsShiftKey: event.shiftKey,
    containsCtrlKey: event.ctrlKey,
    containsAltKey: event.altKey,
    containsMetaKey: event.metaKey,
    containsCapslock: event.getModifierState("CapsLock"),
    shouldIgnore: ignoredKeyCodes.includes(event.keyCode),
  };
};

export const isLetterRequireDeadKey = (
  char: string,
  keyboard: Keyboard,
): boolean => {
  return keyboard?.acceptedWithDeadKey?.includes(char) ?? false;
};

export const isCorrectLetter = (
  letter: string,
  nestedLetter: string,
): boolean => {
  return letter === nestedLetter;
};
// розділяє число пробілами
export const numberWithSpaces = (num: number) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};
// Adding divider line to modal window while scrolling
export const addDividerOnModal = (
  modalBodyElem: React.RefObject<HTMLDivElement>,
  hrElem: React.RefObject<HTMLHRElement>,
) => {
  if (modalBodyElem.current) {
    modalBodyElem.current.addEventListener("scroll", () => {
      if (modalBodyElem.current!.scrollTop >= 6) {
        hrElem.current!.classList.remove("border-transparent");
        hrElem.current!.classList.add("border-brd-primary");
      } else {
        hrElem.current!.classList.remove("border-brd-primary");
        hrElem.current!.classList.add("border-transparent");
      }
    });
  }
};

export const isSameChars = (char1: string, char2: string): boolean => {
  if (char1 === char2) {
    return true;
  }

  const sameChars = ["ʼ‘’'", '“”"„«»'];

  for (let i = 0; i < sameChars.length; i++) {
    const sameCharsRow = sameChars[i];
    if (sameCharsRow.includes(char1) && sameCharsRow.includes(char2)) {
      return true;
    }
  }
  return false;
};
// замінюємо пробіли на unicode
export const spaceToUnicode = (str: string): string =>
  str.replace(/\s/g, "\u00A0");

export function pluralizeDays(count: number, locale: Locales): string {
  const locales: Record<Locales, string[]> = {
    [Locales.en_US]: ["day", "days", "days"],
    [Locales.en_GB]: ["day", "days", "days"],
    [Locales.uk_UA]: ["день", "дні", "днів"],
    [Locales.fr_FR]: ["jour de vente", "jours de soldes", "jours de soldes "],
    [Locales.fr_CA]: ["jour de vente", "jours de soldes", "jours de soldes "],
    [Locales.es_ES]: ["día", "días", "días"],
    [Locales.es_MX]: ["día", "días", "días"],
    [Locales.it_IT]: [
      "giorno di vendita",
      "giorni di vendita",
      "giorni di saldi",
    ],
    [Locales.de_DE]: ["Tag Ausverkauf", "Tage Verkauf", "Tage Verkauf"],
    [Locales.pt_BR]: ["dia", "dias", "dias"],
    [Locales.pl_PL]: ["dzień", "dni", "dni"],
    [Locales.tr_TR]: ["günlük satış", "günlük satış", "günlük indirim"],
    [Locales.nl_NL]: ["dag uitverkoop", "dagen uitverkoop", "dagen korting"],
    [Locales.ru_RU]: ["день", "дня", "дней"],
  };

  let index: number;
  if (count === 1) {
    index = 0;
  } else if (count > 1 && count < 5) {
    index = 1;
  } else {
    index = 2;
  }

  return locales[locale][index];
}

export function normalizeLanguageCode(
  courseLanguage: string | undefined,
): string {
  // Якщо courseLanguage не визначено, повертаємо значення за замовчуванням
  if (typeof courseLanguage === "undefined") {
    return "en";
  }

  // Список мов, для яких потрібно обрізати варіації
  const languagesToNormalize = ["en", "uk"];

  // Перевіряємо, чи початкова частина мови входить в список для нормалізації
  const baseLanguage = courseLanguage.split("_")[0];
  if (languagesToNormalize.includes(baseLanguage)) {
    return baseLanguage;
  }

  // Якщо мова не потребує нормалізації, повертаємо оригінальний код мови
  return courseLanguage;
}

export const getStoredRewardIds = (userId: number): number[] => {
  const storedRewardIdsString = window.sessionStorage.getItem(
    `rewards${userId}`,
  );
  return storedRewardIdsString ? JSON.parse(storedRewardIdsString) : null;
};

export const updateStoredRewardIds = (
  rewardIds: number[],
  userId: number,
): void => {
  window.sessionStorage.setItem(`rewards${userId}`, JSON.stringify(rewardIds));
};

export const rewardsModalContent: Partial<
  Record<RewardEnum, RewardModalContent>
> = {
  [RewardEnum.THREE_EX_LIGHT]: {
    heading: "keyboard-thunder",
    text: "3-exercises-with-lightning",
    nameImg: "keyboard_thunder",
  },
  [RewardEnum.THREE_EX_TARGET]: {
    heading: "accuracy-master",
    text: "3-exercises-with-target",
    nameImg: "accuracy_master",
  },
  [RewardEnum.THREE_EX_STAR]: {
    heading: "typing-star",
    text: "3-exercises-with-star",
    nameImg: "keyboard_star",
  },
  [RewardEnum.FIRST_LESSON]: {
    heading: "first-step",
    text: "complete-first-lesson",
    nameImg: "first_step",
  },
  [RewardEnum.HERO_5]: {
    heading: "collector",
    text: "unlock-5-heroes",
    nameImg: "collector",
  },
  [RewardEnum.STRIX_7_DAY]: {
    heading: "marathoner",
    text: "keep-typing-marathon-for-7-days",
    nameImg: "marathoner",
  },
  [RewardEnum.REWARDS_9]: {
    heading: "achievement-legend",
    text: "legend-of-achievements",
    nameImg: "achievement_legend",
  },
};

export const getOrdinalSuffix = (num: number) => {
  if (num % 10 === 1 && num % 100 !== 11) {
    return "st";
  }
  if (num % 10 === 2 && num % 100 !== 12) {
    return "nd";
  }
  if (num % 10 === 3 && num % 100 !== 13) {
    return "rd";
  }
  return "th";
};
